// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardList from "@insite/content-library/Components/CardList";
import WishListCard, { WishListCardStyles } from "@insite/content-library/Components/WishListCard";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@rugby/App/Components/Notify";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
    isSuggestedLists = "isSuggestedLists",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.isSuggestedLists]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    recentWishListsDataView: getWishListsDataView(state, recentWishListsParameter),
    myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
});

let recentWishListsParameter = {
    page: 1,
    sort: "ModifiedOn DESC",
    expand: ["top3products"],
} as GetWishListsApiParameter;

const mapDispatchToProps = {
    loadWishLists,
    addWishListToCart,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentWishListsStyles {
    wishListCardGridItem?: GridItemProps;
    noWishListsText?: TypographyProps;
    wishListCard?: WishListCardStyles;
    wishListCardWrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
}

const displayType = "none";

export const recentWishListsStyles: RecentWishListsStyles = {
    gridContainer: {
        gap: 40,
        css: css`
            background-color: ${getColor("common.ui3")};

            > div[class^="GridOffset"] {
                margin: 0;
                width: 100%;
                padding: 15px;
            }
        `,
    },
    wishListCardGridItem: {
        width: 12,
        css: css`
            padding: 0;
            margin-bottom: 10px !important;
            && {
                a span {
                    color: gray;
                }
            }
            &:last-child {
                margin-bottom: 0 !important;
            }
        `,
    },
    wishListCardWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
        `,
    },
    wishListCard: {
        actionAddToCartButton: {
            typographyProps: {
                size: 14,
            },
        },

        descriptionGridItem: {
            css: css`
                display: ${displayType};
            `,
        },

        lastUpdatedGridItem: {
            css: css`
                display: ${displayType};
            `,
        },

        extendedInfoGridItem: {
            css: css`
                display: ${displayType};
            `,
        },

        actionGridItem: {
            css: css`
                display: ${displayType};
            `,
        },

        basicInfoGridItem: {
            width: 12,
        },
    },
};

const styles = recentWishListsStyles;

class NavWishLists extends React.Component<Props> {
    componentDidMount() {
        recentWishListsParameter.pageSize = this.props.fields.numberOfRecords;
        if (!this.props.recentWishListsDataView.value && !this.props.recentWishListsDataView.isLoading) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    render() {
        const { recentWishListsDataView: { value: wishLists } } = this.props;
        const recentWishLists = wishLists?.filter(list => {
            if (this.props.fields.isSuggestedLists) {
                return (list.properties.isSuggestedList ?? "").toLowerCase() === "true";
            }
            return (list.properties.isSuggestedList ?? "").toLowerCase() === "false";
        });
        return (
            <CardList data-test-selector="cardListRecentWishLists" {...styles.gridContainer}>
                {recentWishLists && recentWishLists.length === 0 && (
                    <GridItem width={12}>
                        <Notify messageType="info" title="Info" body={siteMessage("WishLists_NoWishlistsFound")} />
                    </GridItem>
                )}
                {recentWishLists &&
                    recentWishLists.map(wishList => (
                        <GridItem
                            width={12}
                            {...styles.wishListCardGridItem}
                            key={wishList.id}
                            data-test-selector="cardListRecentWishLists_container"
                        >
                            <WishListCard
                                wishList={wishList}
                                addWishListToCart={this.props.addWishListToCart}
                                extendedStyles={styles.wishListCard}
                            />
                        </GridItem>
                    ))}
            </CardList>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NavWishLists),
    definition: {
        group: "Xngage Custom" as any,
        icon: "List",
        fieldDefinitions: [
            {
                name: fields.isSuggestedLists,
                displayName: "is Suggested Lists",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
        ]
    },
};

export default widgetModule;
